<template>
  <div
    :class="`formulate-input-element formulate-input-element--${context.type} ${hasRequiredError}`"
    :data-type="context.type"
    @click="handleOpenModal"
  >
    <div
      :class="`mc-input d-inline-flex input-${this.size} ${
        attrs.disabled ? '__disabled' : undefined
      }`"
    >
      <input
        v-if="
          (context.model === '' && valueInput) || (attrs.multiple && context.model.length === 0)
        "
        :placeholder="attrs.placeholder || context.label"
        style="border: 0"
        readonly
      />
      <div v-else-if="multiple" class="d-flex gap-2 flex-wrap">
        <div class="__tag" v-for="(value, index) in fullObject || context.model" :key="index">
          <span>{{ value[displayedBy] }}</span>
          <i @click.stop="handleRemoveTag(value)" class="icon-outline_close" />
        </div>
      </div>
      <div v-else-if="fullObject && displayedBy in fullObject">
        {{ fullObject[displayedBy] }}
      </div>
      <div v-else-if="initialObject && displayedBy in initialObject">
        {{ initialObject[displayedBy] }}
      </div>
      <!-- localFullObject: For DO accurate needs -->
      <div v-else-if="localFullObject && displayedBy in localFullObject">
        {{ localFullObject[displayedBy] }}
      </div>
      <div v-else>
        {{ context.model }}
      </div>

      <i class="icon-fill_menu" />
    </div>

    <b-modal
      v-model="modalShown"
      ref="modalRef"
      centered
      size="lg"
      :title="attrs.modalTitle || $t('global.form.select') + ` ${context.label}`"
      :ok-title="context.okTitle || $t('global.action.apply')"
      :cancel-title="context.cancelTitle || $t('global.action.cancel')"
      header-close-content=""
      cancel-variant="outline-primary"
      @hidden="handleHide"
      @ok="handleApply"
    >
      <div class="mc-value-help">
        <div class="__table-top">
          <h6 class="mc-row-check mt-0" v-if="multiple && selectedValuesLength">
            <span class="badge btn-success">{{ selectedValuesLength }}</span>
            Baris terpilih.
            <a @click="handleUnselectAll" class="color-primary">Bersihkan pilihan</a>
          </h6>

          <b-input-group size="sm" class="mc-has-search mc-input" @click="focusSearch">
            <template #prepend>
              <i class="mc-icon icon-fill_search"></i>
            </template>

            <b-form-input
              :value="search"
              :disabled="isBusy"
              @input="handleSearchChange"
              :placeholder="$t('global.search') + '...'"
              ref="searchInput"
            ></b-form-input>

            <template #append v-if="search">
              <button class="clear-search-btn" :disabled="isBusy" @click="handleClearSearch">
                <i class="pe-2 mc-icon icon-outline_close"></i>
              </button>
            </template>
          </b-input-group>
        </div>
        <div class="__table-wrapper" ref="tableWrapperRef">
          <b-table
            ref="tableRef"
            show-empty
            selectable
            :select-mode="multiple ? 'multi' : 'single'"
            :fields="computedTableFields"
            :items="items"
            :busy="isBusy"
            @row-clicked="handleRowClicked"
            class="-w-auto text-nowrap sticky-head-table border"
          >
            <!-- Slot: Empty -->
            <template #empty>
              <div v-show="!isBusy" class="text-center my-4">Tidak ada data</div>
            </template>

            <!-- Slot: Header Checkbox -->
            <template #head(checkbox)>
              <div v-show="trackBy === 'id'" class="form-check" style="width: 10px">
                <b-form-checkbox
                  :checked="selectedValuesLength == ids.length"
                  @change="handleToggleAll"
                />
              </div>
            </template>

            <!-- Slot: Cell Checkbox -->
            <template #cell(checkbox)="{ index, item }">
              <div class="form-check">
                <b-form-checkbox
                  :checked="selectedValues[item[trackBy]]"
                  @change="handleCheckboxChange($event, index)"
                />
              </div>
            </template>

            <template #head()="data">
              <div @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                    <i class="mc-icon icon-outline_chevron-up"></i>
                  </div>
                  <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                    <i class="mc-icon icon-outline_chevron-down"></i>
                  </div>
                </template>
              </div>
            </template>

            <template #cell()="{ value }">
              {{ value }}
            </template>
          </b-table>
          <div v-observe-visibility="handleElementObserved" style="height: 1px"></div>
          <div v-if="isBusy && hasNextPage" class="px-2 py-2 d-flex gap-2">
            <b-skeleton width="2.5%"></b-skeleton>
            <b-skeleton width="97.5%"></b-skeleton>
          </div>
        </div>

        <div class="__data-count">
          {{ $t('global.text.displays') }} <strong>{{ totalCount || items.length }}</strong>
          {{ $t('global.text.data') }}
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import { isEmpty, isEqual } from 'lodash'

export default {
  components: {},
  props: {
    trackBy: {
      type: String,
      default: 'id',
    },
    displayedBy: {
      type: String,
    },
    listGetter: {
      required: true,
    },
    tableFields: {
      type: Array,
    },
    initialObject: {},
    context: {
      type: Object,
      required: true,
    },
    size: {
      type: String,
      default: 'md',
    },
    // fullObjectValue: For DO accurate needs
    fullObjectValue: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update:fullObjectValue'],
  data() {
    return {
      // An object of selected values,
      // where the key is the trackBy value and the value is true or false
      // e.g. { 'luke': true, 'leia': false, 'han': true }
      selectedValues: {},

      // State to control modal visibility
      modalShown: false,

      // The table items
      items: [],

      // This will be true when the table is loading data from api client
      isBusy: true,

      // We need to make a copy of the 'model' value
      // in case the user cancel the operation
      // and we need to restore the original value
      unsavedModel: null,

      // Sometimes we need to select the id and show other field
      // so we store the full object here
      // TODO: need to check, full object should support object
      // or array of object (for multiple select)
      fullObject: {},

      // Resourceful options
      // - The ids of all items
      ids: [],
      // - The current page
      page: 1,
      // - The limit of items per page
      limit: 10,
      // - The total count of items
      totalCount: null,
      // - Search query
      search: '',
      // - Sort query
      // e.g. [{ key: 'name', order: 'asc' }]
      // needs to be formatted to ['name asc'] before sending to api client
      sorts: [],
    }
  },
  computed: {
    // localFullObject: For DO accurate needs
    localFullObject: {
      set(val) {
        this.$emit('update:fullObjectValue', val)
      },
      get() {
        return this.context.attributes.fullObjectValue || {}
      },
    },
    // localFullObject: For DO accurate needs
    valueInput() {
      return (
        this.localFullObject?.[this.displayedBy] === '' ||
        !(this.displayedBy in this.localFullObject)
      )
    },
    // Shorthand for accessing context attributes
    attrs() {
      return this.context.attributes
    },
    // Shorthand for checking multiple prop
    multiple() {
      return this.attrs.multiple || this.attrs.multiple === ''
    },
    // If supports multiple selection, we need to append a checkbox column
    computedTableFields() {
      if (!this.tableFields) return []

      const tableFields = [...this.tableFields]
      if (this.multiple) {
        tableFields.unshift({
          key: 'checkbox',
          label: '',
        })
      }

      return tableFields
    },
    // Check if there is a next page
    hasNextPage() {
      // If totalCount is null or undefined The data has not been fetched yet
      // so we return true to get the actual total count
      if (!this.totalCount) return true
      return this.page * this.limit < this.totalCount
    },
    // Since the 'selectedValue' is an object, we need to
    // filter it first to count the true selected
    selectedValuesLength() {
      return Object.keys(this.selectedValues).filter((_trackBy) => this.selectedValues[_trackBy])
        .length
    },
    hasRequiredError() {
      if (this.context.visibleValidationErrors.includes('Wajib diisi'))
        return 'formulate-input-error__required'
      return undefined
    },
  },
  watch: {
    initialObject(newValue, oldValue) {
      if (newValue == undefined) {
        this.fullObject = undefined
        return
      }

      // Track by is the key that will be sent to the api client
      if (isEqual(newValue, oldValue)) {
        if (isEmpty(this.fullObject)) this.fullObject = newValue
        return
      }

      const trackBy = this.trackBy

      if (newValue) {
        this.fullObject = newValue
        this.context.model = newValue[trackBy]
      }
    },
  },
  mounted() {
    // Guard checks if the model value is an array when multiple is true
    if (!this.multiple && Array.isArray(this.context.model)) {
      throw new Error('The model value must be an object when the multiple prop is false')
    }

    // Copy the model value to unsavedModel so we can
    // restore it if the user cancel the operation
    this.unsavedModel = this.context.model
  },
  methods: {
    focusSearch() {
      this.$refs['searchInput'].focus()
    },
    handleOpenModal() {
      if (this.attrs.disabled) return
      this.modalShown = true
    },
    async getData(opts, reset = false) {
      this.isBusy = true

      if (!this.listGetter) {
        this.isBusy = false
        return
      }

      const res = await this.listGetter(opts)
      if (!res || res.errors) return

      this.items = reset ? res.data.paginated_result : [...this.items, ...res.data.paginated_result]
      this.ids = res.data.ids
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count

      this.isBusy = false

      await this.$nextTick()
      this.highlightSelectedRow()
    },
    // Used for showing the b-table select color
    // and setting the selectedValues object from the api response
    highlightSelectedRow() {
      if (this.unsavedModel || !isEmpty(this.fullObject)) {
        const trackBy = this.trackBy

        let selected = []
        if (this.unsavedModel) selected = this.multiple ? this.unsavedModel : [this.unsavedModel]
        else if (this.fullObject[trackBy])
          selected = this.multiple ? this.fullObject[trackBy] : [this.fullObject[trackBy]]

        selected.forEach((valueFromModel) => {
          // intentionally used == instead of === because unsavedModel can be a string or number (id or uuid)
          const index = this.items.findIndex((item) => item[trackBy] == valueFromModel)

          // Toggle the row checkbox and update the selectedValues object
          this.$set(this.selectedValues, valueFromModel, true)

          // If item shown on the table, show the b-table select color
          // because selected item is not always shown (server-side pagination)
          if (index != -1) {
            this.$refs.tableRef.selectRow(index)
          }
        })
      }
    },
    handleElementObserved(isVisible) {
      if (!isVisible) return
      if (!this.hasNextPage) return
      // if there is no items and has the search query
      // then we don't have to get data by observer
      if (this.items.length == 0 && this.search) return

      // if total count is null, it means that we haven't fetch the data (initial)
      // do not increment page, but still fetch the data
      this.getData({
        limit: this.limit,
        page: this.totalCount ? ++this.page : this.page,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })

      // Scroll to bottom when pagination is triggered
      const componentRef = this.$refs.tableWrapperRef
      this.$nextTick().then(() => {
        componentRef.scrollTo?.({
          top: componentRef.scrollHeight,
          behavior: 'smooth',
        })
      })
    },
    // if the user clicks on the row (not on checkbox)
    handleRowClicked(item, index) {
      const trackBy = this.trackBy

      // Last checked value before the user clicked on the row
      const checked = this.selectedValues[item[trackBy]]

      // To protect when chexbox state (selectedValues)
      // is not synced with b-table select state
      // Force select/unselect to match the checkbox state
      const toggleRowSelection = !checked ? 'unselectRow' : 'selectRow'
      this.$refs.tableRef[toggleRowSelection](index)

      // If multiple select, only toggle the checkbox and model state
      if (this.multiple) {
        this.$set(this.selectedValues, item[trackBy], !checked)

        this.unsavedModel = Object.keys(this.selectedValues).filter(
          (_trackBy) => this.selectedValues[_trackBy]
        )
      }
      // If single select, overwrite the checkbox and model state
      else {
        this.selectedValues = {
          [item[trackBy]]: !checked,
        }
        this.unsavedModel = checked ? item[trackBy] : null
      }
    },
    // if the user clicks on the checkbox (not on the row)
    handleCheckboxChange(targetChecked, index) {
      if (targetChecked) {
        this.$refs.tableRef.selectRow(index)
      } else {
        this.$refs.tableRef.unselectRow(index)
      }
    },
    handleUnselectAll() {
      this.selectedValues = {}
      this.$refs.tableRef.clearSelected()
    },
    // TODO: Discuss with backend how to handle
    // select all when trackBy is not id
    handleSelectAll() {
      if (this.trackBy === 'id') {
        this.selectedValues = this.ids.reduce((acc, id) => {
          acc[id] = true
          return acc
        }, {})
      } else {
        throw new Error('Select all is not yet implemented when trackBy is not id')
      }
    },
    handleToggleAll(checked) {
      if (checked) {
        this.handleSelectAll()
      } else {
        this.handleUnselectAll()
      }
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts, true)
      this.$refs['searchInput'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange('')
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData(
        {
          limit: this.limit,
          page: 1,
          search: this.search != '' ? this.search : undefined,
          sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        },
        true
      )
    },
    async handleApply() {
      // Filter out the selected values that are false (unselected)
      const trueSelected = Object.keys(this.selectedValues).filter(
        (_trackBy) => this.selectedValues[_trackBy] === true
      )

      // Get details from each selected item
      // however, the items should be loaded on the table
      // TODO: should check what happens if SOME of the items are not loaded
      let fullObject = this.multiple
        ? this.items.filter(
            (item) => trueSelected.includes(item.id) || trueSelected.includes(item.id.toString())
          )
        : this.items.find((item) => item.id == trueSelected[0])

      // If selected item(s) is not found on items, use initial value from parent
      if (!fullObject && trueSelected.length > 0) {
        fullObject = this.initialObject
      }

      this.fullObject = fullObject

      this.context.model = this.multiple ? trueSelected : trueSelected[0]
      this.unsavedModel = null

      this.context.blurHandler()
    },
    handleHide() {
      this.items = []
      this.ids = []
      this.page = 1
      this.totalCount = null
      this.sorts = []
      this.search = ''

      this.unsavedModel = null
      this.selectedValues = {}

      this.context.blurHandler()
    },
    handleRemoveTag(value) {
      if (!this.multiple || !Array.isArray(this.fullObject)) return

      const clickedTagIdx = this.fullObject.findIndex((v) => v[this.trackBy] == value[this.trackBy])
      if (clickedTagIdx === -1) return

      this.fullObject.splice(clickedTagIdx, 1)
      // localFullObject: For DO accurate needs
      this.localFullObject = this.fullObject
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
